import React, { useState } from 'react';
import Layout from '../components/Layout';
import { createProject, createTask, getUsers } from '../api';

// ─────────────────────────────────────────────
// Lightweight CSV parser (handles quoted fields, commas/newlines inside quotes)
// No external dependency needed.
// ─────────────────────────────────────────────
function parseCSV(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], next = text[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') { field += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { field += c; }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.length > 1 || (r.length === 1 && r[0] !== ''));
}

// Common ClickUp export header names → our field keys
const HEADER_GUESSES = {
  project:     ['list name', 'list', 'project', 'space name'],
  title:       ['task name', 'name', 'title'],
  description: ['task content', 'description', 'content', 'notes'],
  status:      ['status'],
  priority:    ['priority'],
  due_date:    ['due date', 'due_date', 'end date'],
  start_date:  ['start date', 'start_date'],
  assignee:    ['assignees', 'assignee', 'assigned to'],
};

const STATUS_MAP = {
  'to do': 'not_started', 'open': 'not_started', 'backlog': 'not_started', 'not started': 'not_started',
  'in progress': 'in_progress', 'doing': 'in_progress', 'active': 'in_progress',
  'complete': 'completed', 'completed': 'completed', 'done': 'completed', 'closed': 'completed',
};
const PRIORITY_MAP = {
  'urgent': 'high', 'high': 'high',
  'normal': 'medium', 'medium': 'medium',
  'low': 'low',
};

const FIELD_LABELS = {
  project:     'Project / List',
  title:       'Task Name *',
  description: 'Description',
  status:      'Status',
  priority:    'Priority',
  due_date:    'Due Date',
  start_date:  'Start Date',
  assignee:    'Assignee',
};

function normalizeDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

export default function ImportPage() {
  const [step,        setStep]        = useState(1);
  const [fileName,    setFileName]    = useState('');
  const [headers,     setHeaders]     = useState([]);
  const [rows,        setRows]        = useState([]);
  const [mapping,     setMapping]     = useState({});
  const [error,       setError]       = useState('');
  const [gpfUsers,    setGpfUsers]    = useState([]);
  const [importing,   setImporting]   = useState(false);
  const [progress,    setProgress]    = useState({ current: 0, total: 0 });
  const [done,        setDone]        = useState(false);
  const [importedCount, setImportedCount] = useState({ projects: 0, tasks: 0 });

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const table = parseCSV(text);
        if (table.length < 2) { setError('This file has no data rows.'); return; }
        const hdrs = table[0].map(h => h.trim());
        const dataRows = table.slice(1);

        // Auto-guess mapping
        const guessed = {};
        Object.entries(HEADER_GUESSES).forEach(([field, aliases]) => {
          const idx = hdrs.findIndex(h => aliases.includes(h.toLowerCase().trim()));
          if (idx !== -1) guessed[field] = idx;
        });

        setHeaders(hdrs);
        setRows(dataRows);
        setMapping(guessed);
        setStep(2);
      } catch (err) {
        setError('Could not read this file. Please make sure it is a valid CSV export.');
      }
    };
    reader.readAsText(file);
  };

  const proceedToPreview = () => {
    if (mapping.title === undefined) { setError('Please map a column to "Task Name" — it is required.'); return; }
    setError('');
    getUsers().then(res => setGpfUsers(res.data || [])).catch(() => {});
    setStep(3);
  };

  const getCell = (row, field) => {
    const idx = mapping[field];
    return idx !== undefined ? (row[idx] || '').trim() : '';
  };

  // Group rows by project/list name for the preview
  const grouped = (() => {
    const map = {};
    rows.forEach(row => {
      const projectName = getCell(row, 'project') || 'Imported Tasks';
      if (!map[projectName]) map[projectName] = [];
      map[projectName].push(row);
    });
    return map;
  })();
  const projectNames = Object.keys(grouped);
  const totalTasks = rows.length;

  const matchUser = (assigneeText) => {
    if (!assigneeText) return null;
    const name = assigneeText.toLowerCase();
    const found = gpfUsers.find(u =>
      (u.name || '').toLowerCase().includes(name) ||
      (u.email || '').toLowerCase() === name
    );
    return found ? found.id : null;
  };

  const startImport = async () => {
    setImporting(true);
    setError('');
    const total = projectNames.length + totalTasks;
    let current = 0;
    setProgress({ current: 0, total });
    let projectsCreated = 0, tasksCreated = 0;

    try {
      for (const projectName of projectNames) {
        let projectId;
        try {
          const res = await createProject({
            title: projectName,
            description: `Imported from ClickUp on ${new Date().toLocaleDateString()}`,
            status: 'active', priority: 'medium', progress: 0,
            assigned_users: [],
          });
          projectId = res.data.id;
          projectsCreated++;
        } catch (e) {
          throw new Error(`Failed to create project "${projectName}": ${e.response?.data?.message || e.message}`);
        }
        current++; setProgress({ current, total });

        for (const row of grouped[projectName]) {
          const title = getCell(row, 'title');
          if (!title) { current++; setProgress({ current, total }); continue; }

          const rawStatus   = getCell(row, 'status').toLowerCase();
          const rawPriority = getCell(row, 'priority').toLowerCase();
          const assigneeId  = matchUser(getCell(row, 'assignee'));

          try {
            await createTask({
              title,
              description: getCell(row, 'description'),
              project_id: projectId,
              status:     STATUS_MAP[rawStatus] || 'not_started',
              priority:   PRIORITY_MAP[rawPriority] || 'medium',
              start_date: normalizeDate(getCell(row, 'start_date')),
              end_date:   normalizeDate(getCell(row, 'due_date')),
              assigned_users: assigneeId ? [assigneeId] : [],
            });
            tasksCreated++;
          } catch (e) {
            console.error(`Failed to import task "${title}":`, e);
          }
          current++; setProgress({ current, total });
        }
      }

      setImportedCount({ projects: projectsCreated, tasks: tasksCreated });
      setDone(true);
    } catch (e) {
      setError(e.message || 'Import failed partway through. Some items may have been created.');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(1); setFileName(''); setHeaders([]); setRows([]); setMapping({});
    setError(''); setDone(false); setImportedCount({ projects: 0, tasks: 0 });
  };

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Import from ClickUp</h1>
          <p style={styles.sub}>Bring your ClickUp tasks into GoProjectFlow using a CSV export</p>
        </div>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        {/* Step 1: Upload */}
        {step === 1 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Step 1 — Export from ClickUp</h3>
            <ol style={styles.instructions}>
              <li>In ClickUp, open the List you want to export</li>
              <li>Click the <strong>⋯</strong> menu → <strong>Export</strong></li>
              <li>Choose <strong>CSV</strong> format and download the file</li>
            </ol>
            <div style={styles.uploadBox}>
              <input type="file" accept=".csv" id="csv-upload" style={{ display: 'none' }} onChange={handleFile} />
              <label htmlFor="csv-upload" style={styles.uploadLabel}>
                📤 Choose CSV File
              </label>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 0 0' }}>.csv files exported from ClickUp</p>
            </div>
          </div>
        )}

        {/* Step 2: Column mapping */}
        {step === 2 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Step 2 — Map Your Columns</h3>
            <p style={styles.cardDesc}>File: <strong>{fileName}</strong> · {rows.length} rows detected</p>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
              We've auto-matched what we could recognize. Confirm or adjust below.
            </p>
            <div style={styles.mappingGrid}>
              {Object.keys(FIELD_LABELS).map(field => (
                <div key={field}>
                  <label style={styles.label}>{FIELD_LABELS[field]}</label>
                  <select
                    style={styles.input}
                    value={mapping[field] !== undefined ? mapping[field] : ''}
                    onChange={e => setMapping({ ...mapping, [field]: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                  >
                    <option value="">— Not mapped —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={styles.formActions}>
              <button style={styles.cancelBtn} onClick={reset}>← Start Over</button>
              <button style={styles.primaryBtn} onClick={proceedToPreview}>Preview Import →</button>
            </div>
          </div>
        )}

        {/* Step 3: Preview + confirm */}
        {step === 3 && !importing && !done && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Step 3 — Review & Import</h3>
            <div style={styles.summaryBox}>
              <div><strong>{projectNames.length}</strong> project{projectNames.length !== 1 ? 's' : ''} will be created</div>
              <div><strong>{totalTasks}</strong> task{totalTasks !== 1 ? 's' : ''} will be imported</div>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 16 }}>
              {projectNames.map(pn => (
                <div key={pn} style={styles.previewProject}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>📁 {pn}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{grouped[pn].length} task(s)</div>
                </div>
              ))}
            </div>
            <div style={styles.formActions}>
              <button style={styles.cancelBtn} onClick={() => setStep(2)}>← Back</button>
              <button style={styles.primaryBtn} onClick={startImport}>Start Import</button>
            </div>
          </div>
        )}

        {/* Importing progress */}
        {importing && (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: '30px 10px' }}>
              <div style={styles.spinner} />
              <p style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Importing your ClickUp data...</p>
              <div style={styles.progressBg}>
                <div style={{ ...styles.progressFill, width: `${(progress.current / progress.total) * 100}%` }} />
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 0 0' }}>
                {progress.current} of {progress.total} items — this can take a few minutes, please don't close this window.
              </p>
            </div>
          </div>
        )}

        {/* Done */}
        {done && (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: '20px 10px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>Import Complete!</h3>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px' }}>
                Created {importedCount.projects} project(s) and {importedCount.tasks} task(s).
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button style={styles.cancelBtn} onClick={reset}>Import Another File</button>
                <a href="/projects" style={{ textDecoration: 'none' }}>
                  <button style={styles.primaryBtn}>View Projects →</button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 760, margin: '0 auto' },
  header:        { marginBottom: 24 },
  title:         { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:           { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  errorBox:      { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' },
  card:          { background: 'white', borderRadius: 12, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardTitle:     { fontSize: 17, fontWeight: 700, margin: '0 0 4px' },
  cardDesc:      { fontSize: 13, color: '#64748b', margin: '0 0 4px' },
  instructions:  { fontSize: 14, color: '#374151', lineHeight: 2, paddingLeft: 20, margin: '16px 0' },
  uploadBox:     { border: '2px dashed #e2e8f0', borderRadius: 12, padding: '40px 20px', textAlign: 'center', marginTop: 20 },
  uploadLabel:   { display: 'inline-block', padding: '12px 28px', background: '#2563eb', color: 'white', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  mappingGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 8 },
  label:         { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:         { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  formActions:   { display: 'flex', justifyContent: 'space-between', marginTop: 24 },
  cancelBtn:     { padding: '10px 20px', border: '1.5px solid #e2e8f0', borderRadius: 9, background: 'white', fontSize: 14, cursor: 'pointer' },
  primaryBtn:    { padding: '10px 22px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  summaryBox:    { display: 'flex', gap: 24, background: '#eff6ff', borderRadius: 10, padding: 16, fontSize: 14, color: '#1e3a8a' },
  previewProject:{ padding: '10px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 8 },
  spinner:       { width: 40, height: 40, border: '4px solid #eff6ff', borderTopColor: '#2563eb', borderRadius: '50%', margin: '0 auto', animation: 'gpf-spin 0.8s linear infinite' },
  progressBg:    { width: '100%', height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  progressFill:  { height: '100%', background: '#2563eb', borderRadius: 99, transition: 'width 0.3s' },
};

if (typeof document !== 'undefined' && !document.getElementById('gpf-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'gpf-spin-keyframes';
  style.textContent = '@keyframes gpf-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}